/**
 * Plan-feature gating (ADR-0041 §11) — show-locked-NOT-hidden. `FeatureGate` renders its
 * children when the tenant has the feature, else a `LockedOverlay` (frosted/blur lock
 * panel) — NOT the global cap modal (which was dismissable + let you use the page). The
 * page content is never rendered when locked (so it can't fire its own 402s); the blur is
 * a decorative skeleton behind the lock card. The backend `require_feature` 402 is the
 * real gate; this is the UX.
 */
import { Lock } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Tooltip } from "@/components/ui/tooltip";
import { useFeature } from "@/lib/entitlements";
import { cn } from "@/lib/cn";

/** Human labels for the known feature axes (fallback: the raw key). */
const FEATURE_LABELS: Record<string, string> = {
  ai_plane: "AI",
  custom_domain: "Dominio personalizzato",
  sso: "Single sign-on",
};

export function featureLabel(key: string): string {
  return FEATURE_LABELS[key] ?? key;
}

/** A frosted lock panel: a faint blurred skeleton backdrop + a centered lock card. */
export function LockedOverlay({ feature }: { feature: string }) {
  const { t } = useTranslation();
  return (
    <div className="relative min-h-[60vh] overflow-hidden rounded-xl border border-border">
      {/* Decorative blurred skeleton — evokes the locked content behind the panel. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 select-none blur-sm">
        <div className="space-y-3 p-6 opacity-40">
          <div className="h-7 w-1/3 rounded bg-elevated" />
          <div className="grid gap-3 sm:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-24 rounded-lg bg-elevated" />
            ))}
          </div>
          <div className="h-40 rounded-lg bg-elevated" />
        </div>
      </div>
      {/* The lock card, centered on a translucent scrim. */}
      <div className="absolute inset-0 grid place-items-center bg-background/40 p-6 backdrop-blur-[2px]">
        <div className="max-w-md rounded-xl border border-border bg-background/90 p-6 text-center shadow-lg">
          <div className="mx-auto mb-3 grid h-11 w-11 place-items-center rounded-full bg-elevated">
            <Lock className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
          </div>
          <h3 className="text-base font-semibold text-foreground">
            {t("billing.upsellTitle", "Non incluso nel tuo piano")}
          </h3>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {t("billing.upsellBody", {
              defaultValue:
                "La funzionalità “{{feature}}” non è disponibile nel piano attuale.",
              feature: featureLabel(feature),
            })}
          </p>
          <Link
            to="/settings?section=billing"
            className="mt-4 inline-flex items-center rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            {t("billing.viewPlan", "Vedi abbonamento")}
          </Link>
        </div>
      </div>
    </div>
  );
}

/** @deprecated kept as an alias — use `LockedOverlay`. */
export const UpsellScreen = LockedOverlay;

export function FeatureGate({
  feature,
  fallback,
  children,
}: {
  feature: string;
  fallback?: React.ReactNode;
  children: React.ReactNode;
}) {
  const allowed = useFeature(feature);
  if (allowed) return <>{children}</>;
  return <>{fallback ?? <LockedOverlay feature={feature} />}</>;
}

/**
 * Wrap a single control (button) so that, when the tenant LACKS `feature`, it renders
 * DISABLED with an explanatory tooltip instead of vanishing — for inline AI actions
 * (the "Proponi con AI" buttons, the Odino launcher) where absence reads worse than a
 * visible-but-locked affordance. When the feature is granted the child is returned
 * untouched. The disabled child gets `pointer-events-none` so the wrapping span (the
 * tooltip trigger) still receives hover — a disabled button alone swallows no events.
 */
export function FeatureTip({
  feature,
  tip,
  children,
}: {
  feature: string;
  tip?: React.ReactNode;
  children: React.ReactElement;
}) {
  const granted = useFeature(feature);
  const { t } = useTranslation();
  if (granted) return children;
  const locked = React.cloneElement(children as React.ReactElement<{ disabled?: boolean; className?: string }>, {
    disabled: true,
    className: cn(
      (children.props as { className?: string }).className,
      "pointer-events-none opacity-60",
    ),
  });
  return (
    <Tooltip
      content={
        tip ?? t("billing.featureLockedTip", "Funzione AI: non inclusa nel tuo abbonamento")
      }
    >
      <span className="inline-flex cursor-not-allowed">{locked}</span>
    </Tooltip>
  );
}
